'use client';

import { buildAndroidExternalBrowserIntent } from '@up2stay/up2promo-commons';
import {
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
} from 'react';

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '../components/drawer';
import { cn } from '../utils/cn';
import {
  getProfileAccentCssVars,
  resolveProfileThemePreference,
  type ProfileTokenPalette,
} from './profile-accent-color';
import type { ProfileThemeMode } from './profile-accent-theme';

export type SaveContactBlockedCopy = {
  title: string;
  description: string;
  genericApp: string;
  iosStepMenu: string;
  iosStepBrowser: string;
  iosStepSave: string;
  openInBrowser: string;
  copyLink: string;
  copied: string;
  copyManually: string;
  linkLabel: string;
};

type SaveContactBlockedSource = {
  [Key in keyof SaveContactBlockedCopy]?: string;
};

const FALLBACK_COPY: SaveContactBlockedCopy = {
  title: 'Save the contact in your browser',
  description:
    '{app} can\'t save this contact directly. Open this page in your browser, then tap "Save contact" again.',
  genericApp: 'This app',
  iosStepMenu: 'Open the app menu (usually ••• at the top right)',
  iosStepBrowser: 'Choose "Open in Safari" or "Open in browser"',
  iosStepSave: 'Tap "Save contact" again',
  openInBrowser: 'Open in browser',
  copyLink: 'Copy link',
  copied: 'Link copied',
  copyManually: 'Select the link and copy it.',
  linkLabel: 'Profile link',
};

function pickCopy(value: string | undefined, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : fallback;
}

export function resolveSaveContactBlockedCopy(
  source?: SaveContactBlockedSource | null,
): SaveContactBlockedCopy {
  return {
    title: pickCopy(source?.title, FALLBACK_COPY.title),
    description: pickCopy(source?.description, FALLBACK_COPY.description),
    genericApp: pickCopy(source?.genericApp, FALLBACK_COPY.genericApp),
    iosStepMenu: pickCopy(source?.iosStepMenu, FALLBACK_COPY.iosStepMenu),
    iosStepBrowser: pickCopy(
      source?.iosStepBrowser,
      FALLBACK_COPY.iosStepBrowser,
    ),
    iosStepSave: pickCopy(source?.iosStepSave, FALLBACK_COPY.iosStepSave),
    openInBrowser: pickCopy(source?.openInBrowser, FALLBACK_COPY.openInBrowser),
    copyLink: pickCopy(source?.copyLink, FALLBACK_COPY.copyLink),
    copied: pickCopy(source?.copied, FALLBACK_COPY.copied),
    copyManually: pickCopy(source?.copyManually, FALLBACK_COPY.copyManually),
    linkLabel: pickCopy(source?.linkLabel, FALLBACK_COPY.linkLabel),
  };
}

function subscribePrefersDark(onStoreChange: () => void) {
  const media = window.matchMedia('(prefers-color-scheme: dark)');
  media.addEventListener('change', onStoreChange);
  return () => media.removeEventListener('change', onStoreChange);
}

function useProfileSheetTheme(
  accentColor: string | null | undefined,
  theme: ProfileThemeMode | null | undefined,
  palette: ProfileTokenPalette,
): { style: CSSProperties; isDark: boolean } {
  const prefersDark = useSyncExternalStore(
    subscribePrefersDark,
    () => window.matchMedia('(prefers-color-scheme: dark)').matches,
    () => true,
  );

  const resolvedTheme = useMemo(
    () => resolveProfileThemePreference(theme, prefersDark),
    [theme, prefersDark],
  );

  const style = useMemo(() => {
    const vars = getProfileAccentCssVars(accentColor, {
      theme: resolvedTheme,
      palette,
    }) as CSSProperties & Record<string, string>;
    return {
      ...vars,
      background: vars['--card'] ?? vars.background,
    };
  }, [accentColor, resolvedTheme, palette]);

  return { style, isDark: resolvedTheme === 'dark' };
}

type SaveContactInAppGuideProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  copy: SaveContactBlockedCopy;
  appLabel: string | null;
  platform: 'ios' | 'android' | 'other';
  pageUrl: string;
  accentColor?: string | null;
  theme?: ProfileThemeMode | null;
  palette?: ProfileTokenPalette;
};

export function SaveContactInAppGuide({
  open,
  onOpenChange,
  copy,
  appLabel,
  platform,
  pageUrl,
  accentColor,
  theme,
  palette = 'standard',
}: SaveContactInAppGuideProps) {
  const linkRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const sheetTheme = useProfileSheetTheme(accentColor, theme, palette);
  const appName = appLabel ?? copy.genericApp;
  const description = copy.description.replaceAll('{app}', appName);
  const intentUrl =
    platform === 'android' && pageUrl
      ? buildAndroidExternalBrowserIntent(pageUrl)
      : null;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setCopied(false);
      setCopyFailed(false);
    }
    onOpenChange(next);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      setCopied(false);
      setCopyFailed(true);
      linkRef.current?.focus();
      linkRef.current?.select();
    }
  };

  const openInBrowser = () => {
    if (!intentUrl) return;
    window.location.assign(intentUrl);
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerContent
        className={cn(
          'border-[var(--border)] bg-[var(--surface)] text-[var(--text)]',
          sheetTheme.isDark && 'dark',
        )}
        style={sheetTheme.style}
      >
        <div className="mx-auto w-full max-w-md pb-2">
          <div className="px-1 pt-2 pb-1">
            <DrawerTitle className="text-center text-lg font-semibold text-[var(--text)]">
              {copy.title}
            </DrawerTitle>
            <DrawerDescription className="mt-2 text-center text-sm text-[var(--muted)]">
              {description}
            </DrawerDescription>
          </div>

          {platform === 'ios' ? (
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-left text-sm text-[var(--text)]">
              <li>{copy.iosStepMenu}</li>
              <li>{copy.iosStepBrowser}</li>
              <li>{copy.iosStepSave}</li>
            </ol>
          ) : null}

          <div className="mt-4 space-y-2">
            <label className="block text-left text-xs font-medium text-[var(--muted)]">
              {copy.linkLabel}
              <input
                ref={linkRef}
                readOnly
                value={pageUrl}
                onFocus={(event) => event.currentTarget.select()}
                className="mt-1.5 w-full rounded-xl border border-[var(--border)] bg-[var(--surface2)] px-3 py-2.5 text-sm text-[var(--text)]"
              />
            </label>
            {copyFailed ? (
              <p className="text-left text-xs text-[var(--muted)]">
                {copy.copyManually}
              </p>
            ) : null}
          </div>

          <div className="mt-4 flex flex-col gap-2.5">
            {intentUrl ? (
              <button
                type="button"
                onClick={openInBrowser}
                className="inline-flex min-h-11 w-full cursor-pointer items-center justify-center rounded-[15px] bg-[image:var(--accent-grad)] px-5 text-sm font-semibold text-[var(--accent-text)]"
              >
                {copy.openInBrowser}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => void copyLink()}
              className="inline-flex min-h-11 w-full cursor-pointer items-center justify-center rounded-[15px] border border-[var(--border)] bg-transparent px-5 text-sm font-semibold text-[var(--text)]"
            >
              {copied ? copy.copied : copy.copyLink}
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
